import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  ViewChild,
} from "@angular/core";
import moment from "moment";
import {
  CalendarView,
  DayCalendarView,
  FiveMinCalendarView,
  HourCalendarView,
  WeekCalendarView,
} from "./calendar-view";
import { ActivatedRoute, Router } from "@angular/router";
import libraryStore from "../../services/stores/library.store";
import { CalendarService } from "../../domain/calendar-service";
import { Show } from "../../domain/show";
import { CalendarSearchService } from "./services/calendar-search.service";
import { Subject, Subscription } from "rxjs";
import { Socket } from "../../infrastructure/socket.service";
import { UserService } from "../../domain/user-service";
import { User } from "../../domain/user";
import { Channel } from "../../domain/channel";
import {
  FuturePreviewViewModel,
  IFuturePreviewApi,
} from "../channel/future-preview/future-preview.view-model";
import { ShowRepository } from "../../domain/show-repository";
import { MediaInfo } from "../../domain/media";
import { MediaRepository } from "../../domain/media-repository";
import { WebRTCPreview } from "../../domain/web-r-t-c-preview";
import { ChannelFuturePreview } from "../../domain/channel-future-preview";
import { CalendarListComponent } from "./calendar-list.component";
import { ICommonUtilInterface } from "../../domain/common/common-util.interface";
import { EditBlockViewModel } from "../blocks/edit-block-header/edit-block.view-model";

@Component({
  selector: "icn-calendar",
  templateUrl: "./calendar.component.html",
  styleUrls: ["./calendar.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CalendarComponent implements OnInit, OnDestroy {
  /* attach store */
  libraryStore = libraryStore;
  currentView: CalendarView;
  pickerIcon = "/assets/icons/picker-icon.svg";
  viewOption = "list";
  activeView;
  prevActiveView;
  refreshOnNow = false;
  lockNow = true;
  searchResultHighlightedUpdated: Subscription;
  currentUser: User;
  currentChannel: Channel;
  readonly VIEW_OPTION_LOCAL_STORAGE: string = "calendar.vo";
  futurePreviewViewModel: FuturePreviewViewModel;
  fromSecEventsPopup = { fromPopup: false, showId: null };
  clientId: string;
  public get isBlockEditor(): boolean {
    return !!this.editBlockVM;
  }
  public showCalendarHeader = true;
  public overrideCurrentChannel: Channel = null;
  public editBlockVM: EditBlockViewModel;

  public _overrideViewOption: string;
  public showCurrentTimeIndicator = true;

  public currentTime: moment.Moment;
  public headerStyle = {};
  public calendarListStyle = { "margin-top": "12px" };
  @ViewChild(CalendarListComponent)
  public calendarListComponent!: CalendarListComponent;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    public calendarService: CalendarService,
    private changeDetectorRef: ChangeDetectorRef,
    private calendarSearchService: CalendarSearchService,
    private socket: Socket,
    public userService: UserService,
    private _mediaRepository: MediaRepository,
    private calendarList: CalendarListComponent,
    private _commonUtils: ICommonUtilInterface,
  ) {}

  extractCurrentTime(t: string) {
    let currentTime;
    if (!t) {
      return moment();
    }

    currentTime = moment(parseInt(t, 10));
    return currentTime.isValid() ? currentTime : moment();
  }

  async ngOnInit() {
    console.log("CalendarComponent->ngOnInit");

    this.clientId = this._commonUtils.generateGuid() + "_schedulerPage";
    this.route.queryParams.subscribe((params: any) => {
      const currentTime = this.extractCurrentTime(params["t"]);

      const viewType = params["vt"];
      switch (viewType) {
        case "week":
          this.currentView = new WeekCalendarView(currentTime);
          this.activeView = "week";
          break;
        case "fiveMin":
          this.currentView = new FiveMinCalendarView(currentTime);
          this.activeView = "fiveMin";
          break;
        case "hour":
          this.currentView = new HourCalendarView(currentTime);
          this.activeView = "hour";
          break;
        case "day":
        default:
          this.currentView = new DayCalendarView(currentTime);
          this.activeView = "day";
          break;
      }
      if (this._overrideViewOption) {
        this.viewOption = this._overrideViewOption;
      } else {
        const viewOptionLocalStorage = localStorage.getItem(
          this.VIEW_OPTION_LOCAL_STORAGE,
        );
        if (viewOptionLocalStorage) {
          this.viewOption = viewOptionLocalStorage;
        }
        const viewOptionParam = params["vo"];
        if (
          viewOptionParam &&
          this.viewOption !== viewOptionParam &&
          (viewOptionParam === "list" || viewOptionParam === "grid")
        ) {
          this.viewOption = viewOptionParam;
        }

        if (this.viewOption === "list" && this.activeView !== "day") {
          this.setViewTypeAndTime("day", currentTime);
        }
      }
    });

    this.currentUser = this.userService.getCurrentUser();
    if (this.overrideCurrentChannel) {
      this.currentChannel = this.overrideCurrentChannel;
    } else {
      this.currentChannel = await this.userService.getCurrentChannel();
    }

    const mediaRepository = this._mediaRepository;
    const socketService = this.socket;
    const $this = this;
    this.futurePreviewViewModel = new FuturePreviewViewModel(
      "Future Preview",
      "50vw",
      "35vw",
      <IFuturePreviewApi>{
        getMediaInfo(mediaId: number): Promise<MediaInfo> {
          return mediaRepository.getMediaInfo(mediaId);
        },
        createChannelFutureWebRTCPreview(
          startTimeUtc: moment.Moment,
          durationMs: number,
        ): Promise<ChannelFuturePreview> {
          return socketService.createChannelFuturePreview(
            $this.currentChannel.id,
            startTimeUtc,
            durationMs,
          );
        },
        deleteFuturePreview(previewId: string): Promise<void> {
          return socketService.deletePreviewChannel(previewId);
        },
        updateFuturePreview(
          previewId: string,
          newStartTimeUtc: moment.Moment,
        ): Promise<void> {
          return socketService.updatePreviewChannel(previewId, newStartTimeUtc);
        },
      },
    );
    this.changeDetectorRef.markForCheck();

    this.searchResultHighlightedUpdated =
      this.calendarSearchService.highlightUpdate.subscribe(() =>
        this.changeDetectorRef.markForCheck(),
      );
  }

  ngOnDestroy() {
    if (this.searchResultHighlightedUpdated) {
      this.searchResultHighlightedUpdated.unsubscribe();
    }
  }

  changeTime(time: moment.Moment) {
    this.setViewTypeAndTime(null, time);
  }

  setViewTypeAndTime(viewType: string = null, time: moment.Moment = null) {
    const params = {
      t: time ? time.valueOf() : this.currentView.currentDate,
    };
    if (viewType) {
      params["vt"] = viewType;
    }

    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: Object.assign({}, this.route.snapshot.queryParams, params),
    });
  }

  isActive(viewType: string): boolean {
    switch (viewType) {
      case "week":
        return this.currentView instanceof WeekCalendarView;
      case "day":
        return this.currentView instanceof DayCalendarView;
      case "fiveMin":
        return this.currentView instanceof FiveMinCalendarView;
      case "hour":
        return this.currentView instanceof HourCalendarView;
    }
  }

  prev() {
    this.changeTime(this.currentView.prev().currentDate);
    this.lockNow = false;
  }

  next() {
    this.changeTime(this.currentView.next().currentDate);
    this.lockNow = false;
  }

  now() {
    const now = moment.utc();
    const withtz = moment(moment.utc(now.format("YYYY-MM-DD HH:mm:ss")));
    this.changeTime(withtz);
    this.refreshOnNow = true;
    this.lockNow = true;
  }

  onCurrentDateChange = function ($event) {
    this.changeTime(
      this.currentView.setCurrentDate($event.currentDate).currentDate,
    );
  };

  onDragHoverTimeout(event) {
    let viewType;
    if (event.durationMs < 1000 * 60 * 5) {
      viewType = "fiveMin";
    } else if (event.durationMs < 1000 * 60 * 60) {
      viewType = "hour";
    } else if (event.durationMs < 1000 * 60 * 60 * 24) {
      viewType = "day";
    } else {
      viewType = "week";
    }

    if (this.isActive(viewType)) {
      return;
    }
    this.setViewTypeAndTime(viewType, event.hoverTime);
  }

  onZoomIn(time: moment.Moment) {
    let viewType;
    if (this.isWeekCurrentView() || this.isDayCalendarView()) {
      viewType = "hour";
    } else if (this.isHourCalendarView()) {
      viewType = "fiveMin";
    } else {
      return;
    }

    this.setViewTypeAndTime(viewType, time);
  }

  isWeekCurrentView() {
    return this.currentView instanceof WeekCalendarView;
  }

  isDayCalendarView() {
    return this.currentView instanceof DayCalendarView;
  }

  isFiveMinCalendarView() {
    return this.currentView instanceof FiveMinCalendarView;
  }

  isHourCalendarView() {
    return this.currentView instanceof HourCalendarView;
  }

  onZoomOut(time: moment.Moment) {
    let viewType;
    if (this.isFiveMinCalendarView()) {
      viewType = "hour";
    } else if (this.isHourCalendarView()) {
      viewType = "day";
    } else if (this.isDayCalendarView()) {
      viewType = "week";
    } else {
      return;
    }

    this.setViewTypeAndTime(viewType, time);
  }

  overrideViewOption(viewOption: string) {
    this._overrideViewOption = viewOption;
    this.viewOption = viewOption;
    this.changeDetectorRef.markForCheck();
  }

  setViewOption(viewOption) {
    if (viewOption === this.viewOption) {
      return;
    }
    localStorage.setItem(this.VIEW_OPTION_LOCAL_STORAGE, viewOption);
    this.viewOption = viewOption;
    const time = this.lockNow ? moment() : this.route.snapshot.queryParams["t"];
    if (viewOption === "list" && this.activeView !== "day") {
      this.prevActiveView = this.activeView;
      this.setViewTypeAndTime("day", time);
      return;
    }
    const vt = this.route.snapshot.queryParams["vt"];
    if (viewOption === "grid" && vt !== this.prevActiveView) {
      this.setViewTypeAndTime(this.prevActiveView, time);
    }
  }

  setViewOptionSecEventPopup(viewOption, showId) {
    this.fromSecEventsPopup = { fromPopup: true, showId: showId };
    this.viewOption = viewOption;
  }

  getCurrentShow(): Show {
    return this.calendarService.currentShow;
  }

  getNextPrerollShows(): Show[] {
    return this.calendarService.nextPrerollShows;
  }

  updateShowToSkipTo(show: Show) {
    return this.calendarService.onSkipToShowSelectedUpdated(
      show.id,
      this.clientId,
    );
  }

  handleCurrentDateChanged(newDate: moment.Moment) {
    this.lockNow = false;

    this.changeTime(newDate);
  }
}
